import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowUpRight, Sparkle, CheckCircle, Globe, TwitterLogo, LinkedinLogo,
  Trash, Eye, ChartLineUp, EnvelopeSimple,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PaymentMethodPicker from "@/components/PaymentMethodPicker";

const EMPTY = {
  name: "", title: "", organization: "", bio: "", image_url: "",
  website: "", twitter: "", linkedin: "", contact_email: "",
  video_reel_url: "", location: "",
  expertise: [], talk_topics: [], languages: [],
  speaking_fee: "", available_for_booking: false,
};

const FEE_OPTIONS = ["Free / pro-bono", "Travel & lodging only", "$1k–$5k", "$5k–$15k", "$15k+"];

export default function SpeakerProfileEditorPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const canceled = search.get("canceled") === "1";

  const [form, setForm] = useState(EMPTY);
  const [serverProfile, setServerProfile] = useState(null); // null = not loaded, false = none yet
  const [pricing, setPricing] = useState(null);
  const [provider, setProvider] = useState(null);   // {id, currency, ...}
  const [providerId, setProviderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [analytics, setAnalytics] = useState(null); // {views, inquiries_count, ...}
  // True when the /me/speaker load itself failed — surface a stale-state pill so
  // users know the form is showing a fresh "new profile" state defensively
  // rather than because they actually have no profile.
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);

  const useSAR = provider?.currency === "SAR";

  useEffect(() => {
    if (canceled) toast.info(t("speakerEditor.checkoutCanceled"));
  }, [canceled, t]);

  useEffect(() => {
    if (!user || !user.role) return;
    // Load /me/speaker independently — a transient failure on featured-pricing
    // or analytics MUST NOT wipe out serverProfile, because that flips
    // `isEdit` to false and the next Save fires POST → backend returns 400
    // "You already have a speaker profile" → user sees "Save failed" and
    // believes creation is broken. Keep the load resilient.
    api.get("/me/speaker")
      .then((p) => {
        setProfileLoadFailed(false);
        if (p.data?.profile) {
          setServerProfile(p.data.profile);
          setForm({ ...EMPTY, ...p.data.profile });
        } else {
          setServerProfile(false);
          setForm((f) => ({ ...f, name: user.name || "", contact_email: user.email || "" }));
        }
      })
      .catch(() => {
        // The profile fetch itself failed — flag it so the UI can warn the
        // user rather than silently switching to "new profile" mode.
        setProfileLoadFailed(true);
        setServerProfile(false);
        setForm((f) => ({ ...f, name: user.name || "", contact_email: user.email || "" }));
      });
    api.get("/me/speaker/featured-pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/me/speaker/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, [user]);

  const isEdit = !!(serverProfile && serverProfile.id);
  const isPublished = serverProfile?.status === "published";
  const isFeatured = !!serverProfile?.is_featured;

  const completion = useMemo(() => {
    const fields = [
      form.name, form.title, form.organization, form.bio,
      form.image_url, form.location, (form.expertise || []).length,
      (form.languages || []).length, form.contact_email,
    ];
    const done = fields.filter((v) => (typeof v === "number" ? v > 0 : (v || "").toString().trim().length > 0)).length;
    return Math.round((done / fields.length) * 100);
  }, [form]);

  function setF(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function csvToArray(s) {
    if (Array.isArray(s)) return s;
    return (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  }

  // Pydantic validation errors arrive as `detail: [{msg, loc, ...}]` — stringify
  // them nicely; everything else falls through to the raw string. Handles the
  // rare case where a custom exception handler returns a plain object.
  function formatErr(e, fallback) {
    const raw = e?.response?.data?.detail;
    if (Array.isArray(raw)) {
      return raw
        .map((x) => (x?.msg ? `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}` : String(x)))
        .join(" · ");
    }
    if (raw && typeof raw === "object") {
      return raw.error || raw.message || JSON.stringify(raw);
    }
    return raw || fallback;
  }

  async function save({ publishAfter } = {}) {
    setBusy(true);
    try {
      const payload = {
        ...form,
        expertise: csvToArray(form.expertise),
        talk_topics: csvToArray(form.talk_topics),
        languages: csvToArray(form.languages),
      };
      let res;
      try {
        res = isEdit
          ? await api.put("/me/speaker", payload)
          : await api.post("/me/speaker", payload);
      } catch (err) {
        // Self-heal: if our local `isEdit` disagrees with the server, recover
        // silently by retrying with the correct verb. This kicks in when a
        // stale/failed profile load flipped `serverProfile` to false while a
        // draft actually exists on the server.
        const status = err?.response?.status;
        const detail = String(err?.response?.data?.detail || "");
        if (status === 400 && detail.toLowerCase().includes("already have a speaker profile")) {
          res = await api.put("/me/speaker", payload);
        } else if (status === 404 && detail.toLowerCase().includes("no speaker profile")) {
          res = await api.post("/me/speaker", payload);
        } else {
          throw err;
        }
      }
      setServerProfile(res.data);
      setForm({ ...EMPTY, ...res.data });
      toast.success(t("speakerEditor.savedToast"));
      if (publishAfter) {
        const pub = await api.post("/me/speaker/publish");
        setServerProfile(pub.data);
        toast.success(t("speakerEditor.publishedToast"));
      }
    } catch (e) {
      toast.error(formatErr(e, t("speakerEditor.saveFailed")));
    } finally { setBusy(false); }
  }

  async function publishOnly() {
    setBusy(true);
    try {
      const res = await api.post("/me/speaker/publish");
      setServerProfile(res.data);
      toast.success(t("speakerEditor.publishedToast"));
    } catch (e) {
      toast.error(formatErr(e, t("speakerEditor.publishFailed")));
    } finally { setBusy(false); }
  }

  async function startFeaturedCheckout() {
    if (!providerId) { toast.error(t("paymentPicker.pickOne")); return; }
    setUpgrading(true);
    try {
      const { data } = await api.post("/me/speaker/featured-checkout", {
        origin_url: window.location.origin,
        provider: providerId,
      });
      if (data.url) window.location.href = data.url;
      else toast.error(t("speakerEditor.noCheckoutUrl"));
    } catch (e) {
      toast.error(formatErr(e, t("speakerEditor.checkoutFailed")));
    } finally { setUpgrading(false); }
  }

  async function deleteProfile() {
    if (!window.confirm(t("speakerEditor.deleteConfirm"))) return;
    setBusy(true);
    try {
      await api.delete("/me/speaker");
      toast.success(t("speakerEditor.deletedToast"));
      nav("/speakers");
    } catch (e) {
      toast.error(formatErr(e, t("speakerEditor.deleteFailed")));
    } finally { setBusy(false); }
  }

  if (loading) {
    return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.loading")}</div>;
  }
  if (!user || !user.role) {
    return (
      <div data-testid="speaker-editor-noauth" className="px-6 md:px-10 lg:px-16 py-16 max-w-md mx-auto text-center">
        <div className="overline mb-3">{t("speakerEditor.breadcrumb")}</div>
        <h1 className="font-display font-black text-4xl mb-3">{t("speakerEditor.signinRequired")}</h1>
        <p className="text-zinc-600 mb-6">{t("speakerEditor.signinBody")}</p>
        <div className="flex gap-3 justify-center">
          <Link to="/login" className="btn-primary">{t("nav.signIn")}</Link>
          <Link to="/register" className="btn-secondary">{t("nav.openAccount")}</Link>
        </div>
      </div>
    );
  }
  if (serverProfile === null) {
    return <div className="px-6 md:px-10 lg:px-16 py-16">{t("common.loading")}</div>;
  }

  return (
    <div data-testid="speaker-editor-page" className="px-6 md:px-10 lg:px-16 py-12 md:py-16 max-w-6xl">
      <div className="overline mb-3">{t("speakerEditor.breadcrumb")}</div>
      <div className="flex items-end justify-between border-b border-zinc-950 pb-5 mb-10 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-[-0.04em] leading-[.92]">
            {isEdit ? t("speakerEditor.editTitle") : t("speakerEditor.newTitle")}
          </h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">{t("speakerEditor.intro")}</p>
        </div>
        {isEdit && (
          <div className="flex flex-wrap items-center gap-3">
            <div data-testid="speaker-status-badge" className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2.5 py-1 border ${isPublished ? (isFeatured ? "bg-blue-700 text-white border-blue-700" : "bg-zinc-950 text-white border-zinc-950") : "border-zinc-300 text-zinc-500"}`}>
              {isFeatured ? t("speakerEditor.statusFeatured") : isPublished ? t("speakerEditor.statusPublished") : t("speakerEditor.statusDraft")}
            </div>
            {isPublished && (
              <Link to={`/speakers/${serverProfile.id}`} data-testid="view-public-profile" className="btn-secondary text-sm" style={{padding:".5rem .9rem"}}>
                <Eye size={14} /> {t("speakerEditor.viewPublic")}
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Stale-state warning — the /me/speaker load failed and the form is showing
          "new profile" state defensively. Loud enough to notice, subtle enough
          not to alarm. Users can hit Refresh to retry the fetch. */}
      {profileLoadFailed && (
        <div
          data-testid="stale-state-warning"
          className="mb-8 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 flex items-start gap-3"
        >
          <span className="text-amber-700 dark:text-amber-400 mt-0.5" aria-hidden>⚠</span>
          <div className="flex-1 text-sm">
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-amber-800 dark:text-amber-300 mb-0.5">
              {t("speakerEditor.staleLoadTitle", { defaultValue: "We couldn't load your saved data" })}
            </div>
            <div className="text-zinc-700 dark:text-zinc-300 leading-relaxed">
              {t("speakerEditor.staleLoadBody", { defaultValue: "You may have an existing profile. Refresh to try loading it again — otherwise saving will start a fresh profile." })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            data-testid="stale-state-refresh"
            className="text-xs font-mono uppercase tracking-[0.14em] px-3 py-1.5 border border-amber-700 text-amber-900 dark:border-amber-500 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 whitespace-nowrap"
          >
            {t("speakerEditor.staleLoadRetry", { defaultValue: "Refresh" })}
          </button>
        </div>
      )}

      <div className="grid md:grid-cols-12 gap-10">
        {/* MAIN FORM */}
        <form data-testid="speaker-editor-form" className="md:col-span-8 space-y-10" onSubmit={(e) => { e.preventDefault(); save(); }}>

          {/* Identity */}
          <section>
            <div className="overline mb-4">{t("speakerEditor.sectionIdentity")}</div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field testid="form-name" label={t("speakerEditor.name")} required>
                <input value={form.name} onChange={(e) => setF("name", e.target.value)} className="input-box" placeholder="Jane Doe" />
              </Field>
              <Field testid="form-title" label={t("speakerEditor.titleField")} required>
                <input value={form.title} onChange={(e) => setF("title", e.target.value)} className="input-box" placeholder="Senior Research Scientist" />
              </Field>
              <Field testid="form-organization" label={t("speakerEditor.organization")}>
                <input value={form.organization} onChange={(e) => setF("organization", e.target.value)} className="input-box" placeholder="MIT CSAIL" />
              </Field>
              <Field testid="form-location" label={t("speakerEditor.location")} hint={t("speakerEditor.locationHint")}>
                <input value={form.location} onChange={(e) => setF("location", e.target.value)} className="input-box" placeholder="Cambridge, USA" />
              </Field>
              <Field testid="form-image" label={t("speakerEditor.photo")} hint={t("speakerEditor.photoHint")} full>
                <div className="flex flex-col md:flex-row gap-2">
                  <input
                    value={form.image_url}
                    onChange={(e) => setF("image_url", e.target.value)}
                    className="input-box flex-1"
                    placeholder="https://…"
                    data-testid="speaker-photo-input"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const { takePhoto, isNative } = await import("@/lib/native");
                      if (!isNative) {
                        // Web fallback: open a hidden file input
                        const inp = document.createElement("input");
                        inp.type = "file";
                        inp.accept = "image/*";
                        inp.capture = "environment";
                        inp.onchange = () => {
                          const f = inp.files?.[0];
                          if (!f) return;
                          const reader = new FileReader();
                          reader.onload = () => setF("image_url", reader.result);
                          reader.readAsDataURL(f);
                        };
                        inp.click();
                        return;
                      }
                      const res = await takePhoto({ source: "prompt", quality: 82 });
                      if (res.dataUrl) setF("image_url", res.dataUrl);
                    }}
                    className="btn-secondary whitespace-nowrap"
                    data-testid="speaker-take-photo-btn"
                  >
                    {t("speakerEditor.takePhoto")}
                  </button>
                </div>
                {form.image_url && form.image_url.startsWith("data:") && (
                  <img
                    src={form.image_url}
                    alt=""
                    className="mt-3 h-24 w-24 object-cover border border-zinc-200"
                  />
                )}
              </Field>
            </div>
          </section>

          {/* Bio + topics */}
          <section>
            <div className="overline mb-4">{t("speakerEditor.sectionBio")}</div>
            <Field testid="form-bio" label={t("speakerEditor.bio")} required hint={t("speakerEditor.bioHint")}>
              <textarea value={form.bio} onChange={(e) => setF("bio", e.target.value)} className="input-box min-h-[140px]" placeholder={t("speakerEditor.bioPlaceholder")} />
            </Field>
            <div className="grid md:grid-cols-2 gap-5 mt-5">
              <Field testid="form-expertise" label={t("speakerEditor.expertise")} hint={t("speakerEditor.csvHint")}>
                <input value={Array.isArray(form.expertise) ? form.expertise.join(", ") : form.expertise} onChange={(e) => setF("expertise", e.target.value)} className="input-box" placeholder="Machine Learning, NLP, RL" />
              </Field>
              <Field testid="form-talktopics" label={t("speakerEditor.talkTopics")} hint={t("speakerEditor.csvHint")}>
                <input value={Array.isArray(form.talk_topics) ? form.talk_topics.join(", ") : form.talk_topics} onChange={(e) => setF("talk_topics", e.target.value)} className="input-box" placeholder="Foundations of LLMs, Alignment, Eval methods" />
              </Field>
              <Field testid="form-languages" label={t("speakerEditor.languages")} hint={t("speakerEditor.csvHint")}>
                <input value={Array.isArray(form.languages) ? form.languages.join(", ") : form.languages} onChange={(e) => setF("languages", e.target.value)} className="input-box" placeholder="English, French, Spanish" />
              </Field>
              <Field testid="form-fee" label={t("speakerEditor.fee")}>
                <select value={form.speaking_fee || ""} onChange={(e) => setF("speaking_fee", e.target.value)} className="input-box">
                  <option value="">{t("speakerEditor.feeNotSet")}</option>
                  {FEE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
            </div>
          </section>

          {/* Links */}
          <section>
            <div className="overline mb-4">{t("speakerEditor.sectionLinks")}</div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field testid="form-website" label={t("speakerEditor.website")} icon={<Globe size={14} />}>
                <input value={form.website} onChange={(e) => setF("website", e.target.value)} className="input-box" placeholder="https://janedoe.com" />
              </Field>
              <Field testid="form-twitter" label={t("speakerEditor.twitter")} icon={<TwitterLogo size={14} />}>
                <input value={form.twitter} onChange={(e) => setF("twitter", e.target.value)} className="input-box" placeholder="@janedoe" />
              </Field>
              <Field testid="form-linkedin" label={t("speakerEditor.linkedin")} icon={<LinkedinLogo size={14} />}>
                <input value={form.linkedin} onChange={(e) => setF("linkedin", e.target.value)} className="input-box" placeholder="https://linkedin.com/in/…" />
              </Field>
              <Field testid="form-reel" label={t("speakerEditor.videoReel")} hint={t("speakerEditor.videoReelHint")}>
                <input value={form.video_reel_url} onChange={(e) => setF("video_reel_url", e.target.value)} className="input-box" placeholder="https://youtu.be/…" />
              </Field>
            </div>
          </section>

          {/* Contact */}
          <section>
            <div className="overline mb-4">{t("speakerEditor.sectionContact")}</div>
            <div className="grid md:grid-cols-2 gap-5">
              <Field testid="form-contact-email" label={t("speakerEditor.contactEmail")} hint={t("speakerEditor.contactEmailHint")}>
                <input type="email" value={form.contact_email} onChange={(e) => setF("contact_email", e.target.value)} className="input-box" placeholder="you@domain.com" />
              </Field>
              <Field testid="form-booking" label={t("speakerEditor.acceptInquiries")}>
                <label className="flex items-center gap-3 mt-2">
                  <input type="checkbox" checked={!!form.available_for_booking} onChange={(e) => setF("available_for_booking", e.target.checked)} className="w-5 h-5 accent-blue-700" />
                  <span className="text-sm text-zinc-700">{t("speakerEditor.acceptInquiriesBody")}</span>
                </label>
              </Field>
            </div>
          </section>

          <div className="border-t border-zinc-200 pt-6 flex flex-wrap items-center gap-3">
            <button data-testid="speaker-save-btn" type="submit" disabled={busy} className="btn-primary">
              {busy ? t("common.loading") : t("speakerEditor.saveDraft")}
            </button>
            {!isPublished && (
              <button data-testid="speaker-save-publish-btn" type="button" disabled={busy} onClick={() => save({ publishAfter: true })} className="btn-accent">
                {t("speakerEditor.saveAndPublish")} <ArrowUpRight size={14} data-arrow="forward" />
              </button>
            )}
            {isPublished && !isFeatured && (
              <button data-testid="speaker-upgrade-btn" type="button" disabled={upgrading || !providerId} onClick={startFeaturedCheckout} className="btn-accent">
                <Sparkle size={14} weight="fill" /> {upgrading ? t("speakerEditor.openingCheckout") : t("speakerEditor.upgradeNow")}
              </button>
            )}
            {isEdit && (
              <button data-testid="speaker-delete-btn" type="button" onClick={deleteProfile} className="ml-auto text-xs font-mono uppercase tracking-wider text-red-600 hover:text-red-800 flex items-center gap-1">
                <Trash size={12} /> {t("speakerEditor.deleteProfile")}
              </button>
            )}
          </div>
        </form>

        {/* SIDEBAR */}
        <aside className="md:col-span-4 space-y-8">
          {/* Completion meter */}
          <div data-testid="completion-card" className="border border-zinc-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">{t("speakerEditor.completion")}</div>
              <div className="font-display font-bold text-2xl tabular">{completion}%</div>
            </div>
            <div className="h-1 bg-zinc-100">
              <div className="h-full bg-blue-700 transition-all" style={{ width: `${completion}%` }} />
            </div>
            <p className="text-xs text-zinc-500 mt-3 leading-relaxed">{t("speakerEditor.completionBody")}</p>
          </div>

          {/* Analytics — Your Reach */}
          {isEdit && analytics && (
            <div data-testid="analytics-card" className="border border-zinc-200 dark:border-zinc-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <ChartLineUp size={14} weight="duotone" className="text-blue-700" />
                <div className="overline">{t("speakerEditor.yourReach") || "Your reach"}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <div className="font-display font-black text-4xl tabular leading-none" data-testid="stat-views">{analytics.views || 0}</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mt-1.5">
                    {t("speakerEditor.profileViews") || "Profile views"}
                  </div>
                </div>
                <div>
                  <div className="font-display font-black text-4xl tabular leading-none" data-testid="stat-inquiries">{analytics.inquiries_count || 0}</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mt-1.5">
                    {t("speakerEditor.inquiries") || "Booking inquiries"}
                  </div>
                </div>
              </div>
              {!isPublished && (
                <p className="text-xs font-mono text-zinc-500 leading-relaxed border-t border-zinc-100 dark:border-zinc-800 pt-3">
                  {t("speakerEditor.publishToTrackReach") || "Publish your profile to start tracking views."}
                </p>
              )}
              {(analytics.recent_inquiries || []).length > 0 && (
                <div className="border-t border-zinc-100 dark:border-zinc-800 pt-4">
                  <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mb-3">
                    {t("speakerEditor.recentInquiries") || "Recent inquiries"}
                  </div>
                  <ul className="space-y-3">
                    {analytics.recent_inquiries.slice(0, 3).map((inq) => (
                      <li key={inq.id} data-testid="recent-inquiry" className="text-xs">
                        <div className="flex items-center gap-1.5">
                          <EnvelopeSimple size={11} className={inq.delivered ? "text-blue-700" : "text-zinc-400"} />
                          <span className="font-mono font-semibold text-zinc-950 dark:text-zinc-100 truncate">{inq.from_name}</span>
                          {inq.from_organization && (
                            <span className="text-zinc-500 truncate">· {inq.from_organization}</span>
                          )}
                        </div>
                        <p className="mt-1 text-zinc-600 dark:text-zinc-400 leading-relaxed line-clamp-2">
                          {inq.message}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(analytics.inquiries_count || 0) > 0 && (
                <Link
                  to="/speakers/me/inbox"
                  data-testid="analytics-view-inbox"
                  className="mt-4 text-xs font-mono uppercase tracking-[0.14em] text-blue-700 hover:underline inline-flex items-center gap-1"
                >
                  {t("speakerEditor.viewAllInquiries", { defaultValue: "View all inquiries" })} →
                </Link>
              )}
            </div>
          )}

          {/* Featured upgrade card */}
          {pricing && (
            <div data-testid="featured-upgrade-card" className={`border-2 p-6 relative ${isFeatured ? "border-blue-700 bg-blue-50/40" : "border-zinc-950"}`}>
              <div className={`overline flex items-center gap-1.5 mb-2 ${isFeatured ? "text-blue-700" : "text-zinc-950"}`}>
                <Sparkle size={11} weight="fill" /> {isFeatured ? t("speakerEditor.featuredActive") : t("speakerEditor.featuredUpsell")}
              </div>
              <div className="font-display font-black text-5xl tracking-tight tabular leading-none mb-1">
                {useSAR ? `SAR ${(pricing.price_sar ?? pricing.price_usd * 3.75).toFixed(0)}` : `$${pricing.price_usd?.toFixed(0)}`}
              </div>
              <div className="text-xs font-mono text-zinc-500 mb-5">{pricing.days} {t("speakerEditor.days")} · {useSAR ? "SAR" : "USD"} · {t("speakerEditor.oneTime")}</div>
              <ul className="space-y-2 text-sm">
                {pricing.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-blue-700 mt-0.5 flex-shrink-0" /> <span className="text-zinc-700">{p}</span></li>
                ))}
              </ul>
              {isFeatured && serverProfile.featured_until && (
                <div className="mt-5 pt-4 border-t border-zinc-200 text-xs font-mono text-zinc-500">
                  {t("speakerEditor.activeThrough")} <span className="text-zinc-950">{new Date(serverProfile.featured_until).toLocaleDateString()}</span>
                </div>
              )}
              {!isFeatured && (
                isPublished ? (
                  <div className="mt-5 space-y-4">
                    <PaymentMethodPicker
                      value={providerId}
                      onChange={(id, p) => { setProviderId(id); setProvider(p); }}
                      dataTestidPrefix="speaker-pay"
                    />
                    <button data-testid="sidebar-upgrade-btn" type="button" onClick={startFeaturedCheckout} disabled={upgrading || !providerId} className="btn-primary w-full justify-center">
                      {upgrading ? t("speakerEditor.openingCheckout") : t("speakerEditor.upgradeNow")}
                    </button>
                  </div>
                ) : (
                  <p className="mt-5 text-xs font-mono text-zinc-500 leading-relaxed">{t("speakerEditor.upgradeAfterPublish")}</p>
                )
              )}
            </div>
          )}

          {/* Tips */}
          <div className="text-xs font-mono text-zinc-500 leading-relaxed space-y-2">
            <div className="overline text-zinc-950 mb-2">{t("speakerEditor.tipsTitle")}</div>
            <p>· {t("speakerEditor.tip1")}</p>
            <p>· {t("speakerEditor.tip2")}</p>
            <p>· {t("speakerEditor.tip3")}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, hint, icon, required, full, children, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""} data-testid={testid}>
      <label className="overline flex items-center gap-1.5 mb-1.5">
        {icon}
        <span>{label}{required && <span className="text-red-600 ms-1">*</span>}</span>
      </label>
      {children}
      {hint && <div className="text-xs font-mono text-zinc-400 mt-1.5">{hint}</div>}
    </div>
  );
}
